import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Database schema definition
interface SiafoDBSchema extends DBSchema {
  'offline-inspections': {
    key: string;
    value: {
      id: string;
      agendaInspecaoId: string;
      professionalId: string;
      checklistJson: Record<string, unknown>;
      fotos: string[];
      checkIn: string;
      checkOut?: string;
      offlineJustification?: string;
      syncStatus: 'pending' | 'syncing' | 'synced' | 'failed';
      createdAt: string;
      updatedAt: string;
      retryCount: number;
    };
    indexes: { 'by-sync-status': string; 'by-agenda': string };
  };
  'cached-data': {
    key: string;
    value: {
      key: string;
      data: unknown;
      timestamp: number;
      expiresAt: number;
    };
    indexes: { 'by-timestamp': number };
  };
  'sync-queue': {
    key: string;
    value: {
      id: string;
      type: 'inspection' | 'photo' | 'checklist';
      payload: unknown;
      priority: number;
      createdAt: string;
      retryCount: number;
      lastError?: string;
    };
    indexes: { 'by-priority': number; 'by-type': string };
  };
}

const DB_NAME = 'siafo-offline-db';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<SiafoDBSchema> | null = null;

export async function getDB(): Promise<IDBPDatabase<SiafoDBSchema>> {
  if (dbInstance) return dbInstance;
  
  dbInstance = await openDB<SiafoDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Offline inspections store
      if (!db.objectStoreNames.contains('offline-inspections')) {
        const inspectionStore = db.createObjectStore('offline-inspections', { keyPath: 'id' });
        inspectionStore.createIndex('by-sync-status', 'syncStatus');
        inspectionStore.createIndex('by-agenda', 'agendaInspecaoId');
      }
      
      // Cached data store
      if (!db.objectStoreNames.contains('cached-data')) {
        const cacheStore = db.createObjectStore('cached-data', { keyPath: 'key' });
        cacheStore.createIndex('by-timestamp', 'timestamp');
      }
      
      // Sync queue store
      if (!db.objectStoreNames.contains('sync-queue')) {
        const syncStore = db.createObjectStore('sync-queue', { keyPath: 'id' });
        syncStore.createIndex('by-priority', 'priority');
        syncStore.createIndex('by-type', 'type');
      }
    },
  });
  
  return dbInstance;
}

// Offline Inspections
export async function saveOfflineInspection(inspection: SiafoDBSchema['offline-inspections']['value']) {
  const db = await getDB();
  await db.put('offline-inspections', {
    ...inspection,
    updatedAt: new Date().toISOString(),
  });
}

export async function getOfflineInspection(id: string) {
  const db = await getDB();
  return db.get('offline-inspections', id);
}

export async function getPendingInspections() {
  const db = await getDB();
  return db.getAllFromIndex('offline-inspections', 'by-sync-status', 'pending');
}

export async function updateInspectionSyncStatus(
  id: string, 
  status: 'pending' | 'syncing' | 'synced' | 'failed',
  error?: string
) {
  const db = await getDB();
  const inspection = await db.get('offline-inspections', id);
  if (inspection) {
    await db.put('offline-inspections', {
      ...inspection,
      syncStatus: status,
      retryCount: status === 'failed' ? inspection.retryCount + 1 : inspection.retryCount,
      updatedAt: new Date().toISOString(),
    });
  }
}

export async function deleteOfflineInspection(id: string) {
  const db = await getDB();
  await db.delete('offline-inspections', id);
}

// Cached Data
export async function cacheData(key: string, data: unknown, ttlMinutes: number = 60) {
  const db = await getDB();
  const now = Date.now();
  await db.put('cached-data', {
    key,
    data,
    timestamp: now,
    expiresAt: now + (ttlMinutes * 60 * 1000),
  });
}

export async function getCachedData<T>(key: string): Promise<{ data: T; timestamp: number; isExpired: boolean } | null> {
  const db = await getDB();
  const cached = await db.get('cached-data', key);
  
  if (!cached) return null;
  
  return {
    data: cached.data as T,
    timestamp: cached.timestamp,
    isExpired: Date.now() > cached.expiresAt,
  };
}

export async function clearExpiredCache() {
  const db = await getDB();
  const now = Date.now();
  const tx = db.transaction('cached-data', 'readwrite');
  const store = tx.objectStore('cached-data');
  
  let cursor = await store.openCursor();
  while (cursor) {
    if (cursor.value.expiresAt < now) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }
  
  await tx.done;
}

// Sync Queue
export async function addToSyncQueue(item: Omit<SiafoDBSchema['sync-queue']['value'], 'id' | 'createdAt' | 'retryCount'>) {
  const db = await getDB();
  const id = `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  await db.put('sync-queue', {
    ...item,
    id,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  });
  return id;
}

export async function getSyncQueue() {
  const db = await getDB();
  return db.getAllFromIndex('sync-queue', 'by-priority');
}

export async function removeFromSyncQueue(id: string) {
  const db = await getDB();
  await db.delete('sync-queue', id);
}

export async function clearSyncQueue() {
  const db = await getDB();
  await db.clear('sync-queue');
}

// Utility
export async function getDBStats() {
  const db = await getDB();
  const [pendingInspections, cachedItems, syncQueue] = await Promise.all([
    db.count('offline-inspections'),
    db.count('cached-data'),
    db.count('sync-queue'),
  ]);
  
  return {
    pendingInspections,
    cachedItems,
    syncQueue,
  };
}
